import React, { useState } from 'react';
import { useIntl } from "react-intl";
import { SceneTemplate, SceneTemplateCreate } from "@/model";
import { useCreateTemplate } from '@/query/template';
import { useModal, useScenarioId } from "@/hook";
import Button from "@/component/Button";
import { Modal } from "@/component/modal";
import { TextInputGroup } from "@/component/input";
import { successToast, errorToast } from '@/component/Toast';


export const TEMPLATE_CREATE_MODAL_ID = "template-create-Modal-id";

interface TemplateCreateModalProps {
    checkNameUsedByOthers: (name: string) => boolean;
    onCreate: (template: SceneTemplate) => void;
}

const TemplateCreateModal: React.FC<TemplateCreateModalProps> = (props) => {
    const { checkNameUsedByOthers, onCreate } = props;

    const intl = useIntl();
    const modal = useModal();
    const scenarioId = useScenarioId();

    const [name, setName] = useState<string>("");
    const [isValidated, setIsValidated] = useState<boolean>(false);
    const [isNameUsed, setIsNameUsed] = useState<boolean>(false);

    const { mutateAsync: createTemplate, isPending: isCreating } = useCreateTemplate(scenarioId);

    const submit = async () => {
        setIsValidated(true);

        const form: SceneTemplateCreate = {
            name: name.trim(),
            paragraphs: []
        };
        if (!form.name || isNameUsed){
            return;
        }
        createTemplate(form)
            .then((newTemplate) => {
                successToast(intl.formatMessage({ id: "t001" }));
                onCreate(newTemplate);
                modal.close(TEMPLATE_CREATE_MODAL_ID);
            })
            .catch(() => {
                errorToast(intl.formatMessage({ id: "t002" }));
            });
    };

    const onChangeName = (value: string) => {
        setName(value);
        setIsNameUsed(checkNameUsedByOthers?.(value.trim()));
    };

    const onClickCancel = (e: React.MouseEvent) => {
        e.preventDefault();
        modal.close(TEMPLATE_CREATE_MODAL_ID);
    };

    const onClickConfirm = async (e: React.MouseEvent) => {
        e.preventDefault();
        await submit();
    };

    const onClose = () => {
        setIsValidated(false);
        setIsNameUsed(false);
        setName("");
    };

    return (
        <Modal id={TEMPLATE_CREATE_MODAL_ID}
               size="sm"
               title={intl.formatMessage({ id: "i03001" })}
               closeButtonAriaLabel={intl.formatMessage({ id: "b0003" })}
               onClose={onClose}
        >
            <div className="modal-body gap-y-8">
                <div className="modal-description">
                    <TextInputGroup required
                                    useDirty
                                    value={name}
                                    label={intl.formatMessage({ id: "b0021" })}
                                    placeholder={intl.formatMessage({ id: "i03003" })}
                                    invalid={(isValidated && !name.trim()) || isNameUsed}
                                    invalidText={isNameUsed ? intl.formatMessage({ id: "i03007" }) : undefined}
                                    onChange={onChangeName}
                    />
                </div>

                <div className="modal-btn-group">
                    <Button size="sm"
                            variant="secondary"
                            onClick={onClickCancel}
                    >
                        {intl.formatMessage({ id: "b0002" })}
                    </Button>
                    <Button size="sm"
                            variant="primary"
                            disabled={isCreating || isNameUsed || !name.trim()}
                            onClick={onClickConfirm}
                    >
                        {intl.formatMessage({ id: "b0001" })}
                    </Button>
                </div>
            </div>
        </Modal>
    );
};

export default TemplateCreateModal;
